export interface TenantThemePalette {
  light: Record<string, string>;
  dark: Record<string, string>;
}

export interface TenantBranding {
  id: string;
  displayName: string;
  documentTitle?: string;
  favicon?: string;
  logos: {
    light: string;
    dark: string;
    login?: string;
  };
  palette: TenantThemePalette;
}

/** Favicon usado quando o tenant não declara um próprio. */
export const DEFAULT_FAVICON = 'assets/favicon_io/favicon.ico';

export const DEFAULT_TENANT_ID = 'dev-manager';
export const TENANT_STORAGE_KEY = 'portal-tenant-id';

const defaultLightPalette: Record<string, string> = {
  '--app-primary-color': '#004600',
  '--primary-color': '#004600',
  '--color-zitec-darkest': '#0e2e1c',
  '--color-zitec-dark': '#434d45',
  '--color-zitec-primary': '#004600',
  '--color-zitec-medium': '#6b9749',
  '--color-zitec-light': '#96b346',
  '--text-heading': '#004600',
  '--hover-light-green': '#e6f3e6',
  '--active-bg': 'rgba(68, 112, 54, 0.08)',
  '--mat-fab-container-color': '#cce7cc',
  '--mat-fab-foreground-color': '#00682f',
  '--mat-fab-small-container-color': '#cce7cc',
  '--mat-fab-small-foreground-color': '#00682f'
};

const defaultDarkPalette: Record<string, string> = {
  '--app-primary-color': '#6b9749',
  '--primary-color': '#6b9749',
  '--color-zitec-darkest': '#07140e',
  '--color-zitec-dark': '#224634',
  '--color-zitec-primary': '#6b9749',
  '--color-zitec-medium': '#88b864',
  '--color-zitec-light': '#b8d48c',
  '--text-heading': '#4fb346',
  '--hover-light-green': 'rgba(70, 179, 79, 0.329)',
  '--active-bg': 'rgba(150, 179, 70, 0.15)',
  '--mat-fab-container-color': '#4fb346',
  '--mat-fab-foreground-color': '#0d1117',
  '--mat-fab-small-container-color': '#4fb346',
  '--mat-fab-small-foreground-color': '#0d1117'
};

export const TENANT_BRANDING: Record<string, TenantBranding> = {
  'dev-manager': {
    id: 'dev-manager',
    displayName: 'Zitec',
    documentTitle: 'Portal Zitec',
    favicon: DEFAULT_FAVICON,
    logos: {
      light: 'assets/images/zitec/zi_bg_white_2.jpg',
      dark: 'assets/images/zitec/zi_logo_darkmode.png',
      login: 'assets/images/zitec/logo-zitec-transparent.png'
    },
    palette: {
      light: defaultLightPalette,
      dark: defaultDarkPalette
    }
  },
  giaa: {
    id: 'giaa',
    displayName: 'GIAA',
    documentTitle: 'Portal GIAA',
    // Sem favicon próprio: reaproveita o logo até o time de design entregar um .ico/.png quadrado.
    favicon: 'assets/images/giaa/giaa-logo-verde.png',
    logos: {
      light: 'assets/images/giaa/giaa-logo-verde-vazada.png',
      dark: 'assets/images/giaa/giaa-logo-darkmode.png',
      login: 'assets/images/giaa/giaa-logo-darkmode.png'
    },
    palette: {
      light: {
        ...defaultLightPalette,
        '--app-primary-color': '#1f6b4f',
        '--primary-color': '#1f6b4f',
        '--color-zitec-darkest': '#0e2f22',
        '--color-zitec-dark': '#275742',
        '--color-zitec-primary': '#1f6b4f',
        '--color-zitec-medium': '#3d8b68',
        '--color-zitec-light': '#88c9a5',
        '--text-heading': '#1f6b4f',
        '--hover-light-green': '#e5f4ec',
        '--active-bg': 'rgba(31, 107, 79, 0.12)',
        '--mat-fab-container-color': '#d6efe2',
        '--mat-fab-foreground-color': '#1f6b4f',
        '--mat-fab-small-container-color': '#d6efe2',
        '--mat-fab-small-foreground-color': '#1f6b4f'
      },
      dark: {
        ...defaultDarkPalette,
        '--app-primary-color': '#7fd0a4',
        '--primary-color': '#7fd0a4',
        '--color-zitec-darkest': '#081711',
        '--color-zitec-dark': '#173c2c',
        '--color-zitec-primary': '#59b785',
        '--color-zitec-medium': '#7fd0a4',
        '--color-zitec-light': '#a5e2c1',
        '--text-heading': '#7fd0a4',
        '--hover-light-green': 'rgba(127, 208, 164, 0.18)',
        '--active-bg': 'rgba(89, 183, 133, 0.18)',
        '--mat-fab-container-color': '#59b785',
        '--mat-fab-foreground-color': '#081711',
        '--mat-fab-small-container-color': '#59b785',
        '--mat-fab-small-foreground-color': '#081711'
      }
    }
  },
  'fjord-wealth': {
    id: 'fjord-wealth',
    displayName: 'Fjord Wealth',
    documentTitle: 'Portal Fjord Wealth',
    favicon: 'assets/images/fjord/fjord-logo-transparent.png',
    logos: {
      light: 'assets/images/fjord/fjord-logo-transparent.png',
      dark: 'assets/images/fjord/fjord-logo-darkmode.png',
      login: 'assets/images/fjord/fjord-logo-darkmode.png'
    },
    palette: {
      light: defaultLightPalette,
      dark: defaultDarkPalette
    }
  },
  // Rebrand: ID FIP agora é Horizon Trust (Manual da Marca v1.0 — ouro/preto/neutros quentes).
  // O id interno permanece 'id-fip' (gates de comportamento e X-Tenant-Id do backend);
  // o subdomínio novo é resolvido via TENANT_HOST_ALIASES.
  'id-fip': {
    id: 'id-fip',
    displayName: 'Horizon Trust',
    documentTitle: 'Horizon Trust',
    favicon: 'assets/images/horizon-trust/horizon-trust-favicon.png',
    logos: {
      light: 'assets/images/horizon-trust/horizon-trust-logo.png',
      dark: 'assets/images/horizon-trust/horizon-trust-logo-dark-mode.png',
      login: 'assets/images/horizon-trust/horizon-trust-logo-dark-mode.png'
    },
    palette: {
      light: {
        ...defaultLightPalette,
        '--app-primary-color': '#C6A25A',
        '--primary-color': '#C6A25A',
        '--color-zitec-darkest': '#141514',
        '--color-zitec-dark': '#1F2120',
        '--color-zitec-primary': '#C6A25A',
        '--color-zitec-medium': '#8C6D2F',
        '--color-zitec-light': '#E9EBEB',
        '--text-heading': '#141514',
        '--hover-light-green': '#F4F5F5',
        '--active-bg': 'rgba(198, 162, 90, 0.12)',
        '--bg-body': '#F4F5F5',
        '--bg-paper': '#FBFCFC',
        '--border-color': '#DDE1E1',
        '--hover-bg': '#E9EBEB',
        '--text-primary': '#1F2120',
        '--text-secondary': '#4C5354',
        '--border-strong': '#C0C7C7',
        '--scrollbar-thumb': '#C0C7C7',
        '--mat-fab-container-color': '#141514',
        '--mat-fab-foreground-color': '#C6A25A',
        '--mat-fab-small-container-color': '#141514',
        '--mat-fab-small-foreground-color': '#C6A25A',
        '--table-header-gradient': 'linear-gradient(180deg, rgba(244, 245, 245, 0.95) 0%, rgba(233, 235, 235, 0.95) 100%)',
        '--gradient-brand': 'linear-gradient(135deg, #8C6D2F 0%, #C6A25A 100%)',
        '--auth-bg-start': '#141514',
        '--auth-bg-end': '#1F2120',
        '--auth-btn-bg': '#C6A25A',
        '--auth-btn-fg': '#141514',
        '--auth-btn-hover-bg': '#8C6D2F',
        '--btn-primary-fg': '#141514',
        '--status-success': '#2F6B4F',
        '--status-success-bg': '#EDF3F0',
        '--status-info': '#2C5670',
        '--status-info-bg': '#EDF1F4',
        '--status-warning': '#A9691C',
        '--status-warning-bg': '#F7F0E6',
        '--status-error': '#9C3A32',
        '--status-error-bg': '#F6EDEC',
        '--status-neutral': '#8A9294',
        '--status-neutral-bg': '#F0F2F2',
        '--font-heading': "'Archivo', sans-serif",
        '--font-body': "'Libre Franklin', Roboto, sans-serif"
      },
      dark: {
        ...defaultDarkPalette,
        '--app-primary-color': '#D8B978',
        '--primary-color': '#D8B978',
        '--color-zitec-darkest': '#141514',
        '--color-zitec-dark': '#1F2120',
        '--color-zitec-primary': '#D8B978',
        '--color-zitec-medium': '#C6A25A',
        '--color-zitec-light': '#4C5354',
        '--text-heading': '#FBFCFC',
        '--hover-light-green': 'rgba(216, 185, 120, 0.18)',
        '--active-bg': 'rgba(216, 185, 120, 0.15)',
        '--bg-body': '#141514',
        '--bg-paper': '#1F2120',
        '--border-color': '#4C5354',
        '--hover-bg': '#1F2120',
        '--text-primary': '#F4F5F5',
        '--text-secondary': '#C0C7C7',
        '--border-strong': '#8A9294',
        '--scrollbar-thumb': '#8A9294',
        '--mat-fab-container-color': '#D8B978',
        '--mat-fab-foreground-color': '#141514',
        '--mat-fab-small-container-color': '#D8B978',
        '--mat-fab-small-foreground-color': '#141514',
        '--table-header-gradient': 'linear-gradient(180deg, rgba(216, 185, 120, 0.14) 0%, rgba(216, 185, 120, 0.06) 100%)',
        '--gradient-brand': 'linear-gradient(135deg, #8C6D2F 0%, #D8B978 100%)',
        '--auth-bg-start': '#141514',
        '--auth-bg-end': '#1F2120',
        '--auth-btn-bg': '#D8B978',
        '--auth-btn-fg': '#141514',
        '--auth-btn-hover-bg': '#C6A25A',
        '--btn-primary-fg': '#141514',
        '--status-success': '#5E9E80',
        '--status-success-bg': 'rgba(94, 158, 128, 0.16)',
        '--status-info': '#6E9DBC',
        '--status-info-bg': 'rgba(110, 157, 188, 0.16)',
        '--status-warning': '#D69A4E',
        '--status-warning-bg': 'rgba(214, 154, 78, 0.16)',
        '--status-error': '#C97B72',
        '--status-error-bg': 'rgba(201, 123, 114, 0.16)',
        '--status-neutral': '#8A9294',
        '--status-neutral-bg': 'rgba(138, 146, 148, 0.16)',
        '--font-heading': "'Archivo', sans-serif",
        '--font-body': "'Libre Franklin', Roboto, sans-serif"
      }
    }
  }
};

// Subdomínios alternativos que resolvem para um tenant já registrado (rebrands):
// mantém o id canônico nos gates de comportamento e no header X-Tenant-Id.
const TENANT_HOST_ALIASES: Record<string, string> = {
  'horizon-trust': 'id-fip'
};

export function extractTenantIdFromHostname(hostname: string): string {
  const normalizedHostname = hostname.toLowerCase().trim();

  if (!normalizedHostname || normalizedHostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(normalizedHostname)) {
    return DEFAULT_TENANT_ID;
  }

  const parts = normalizedHostname.split('.');

  if (parts.length < 3) {
    return DEFAULT_TENANT_ID;
  }

  const subdomain = parts[0] || DEFAULT_TENANT_ID;
  return TENANT_HOST_ALIASES[subdomain] ?? subdomain;
}

export function resolveTenantBranding(tenantId: string): TenantBranding {
  const branding = TENANT_BRANDING[tenantId];
  if (branding) return branding;

  const displayName = tenantId.toUpperCase();
  return {
    ...TENANT_BRANDING[DEFAULT_TENANT_ID],
    id: tenantId,
    displayName,
    // Tenant desconhecido herda o visual padrão, mas não o título/ícone da Zitec:
    // o título deriva do próprio id e o favicon volta ao neutro.
    documentTitle: `Portal ${displayName}`,
    favicon: undefined
  };
}

/** Título da aba para um tenant, com o fallback `Portal ${displayName}`. */
export function resolveDocumentTitle(branding: TenantBranding): string {
  return branding.documentTitle ?? `Portal ${branding.displayName}`;
}